import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { crashReportsApi } from '@api/endpoints/crashReports'
import type { CrashGroupQuery, CrashReportQuery } from '@api/types/crashReports'

/** Query key root, so clearing a group can refresh every crash view at once. */
const ROOT = 'crash-reports'

/** New reports arrive whenever a phone next opens the app; a minute is fresh enough. */
const REFRESH_MS = 60_000

export function useCrashGroups(params: CrashGroupQuery) {
  return useQuery({
    queryKey: [ROOT, 'groups', params.days, params.platform ?? '', params.appVersion ?? ''],
    queryFn: () => crashReportsApi.getGroups(params),
    refetchInterval: REFRESH_MS,
  })
}

export function useCrashReports(params: CrashReportQuery, enabled = true) {
  return useQuery({
    queryKey: [ROOT, 'list', params.fingerprint ?? '', params.userId ?? '', params.page, params.pageSize],
    queryFn: () => crashReportsApi.getReports(params),
    placeholderData: keepPreviousData,
    refetchInterval: REFRESH_MS,
    enabled,
  })
}

/** One report with its stacks. Only fetched while its dialog is open. */
export function useCrashReport(id: string | null) {
  return useQuery({
    queryKey: [ROOT, 'detail', id],
    queryFn: () => crashReportsApi.getReport(id as string),
    enabled: id != null,
  })
}

export function useClearCrashGroup() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (fingerprint: string) => crashReportsApi.clearGroup(fingerprint),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [ROOT] }),
  })
}
